import { createSlice } from "@reduxjs/toolkit";
import {
  syncPushNotificationDeviceAndTopics,
  listPushNotificationDevices,
  listPushNotificationTopics,
  deletePushNotificationDevice,
  subscribeToPushNotificationTopic,
  unsubscribeFromPushNotificationTopic,
  subscribeToAllPushNotificationTopics,
  unsubscribeFromAllPushNotificationTopics,
} from "../actions/profilePushNotificationsActions";

const initialState = {
  isLoading: false,
  error: null,

  providerDeviceId: null,
  devices: [],
  deviceIds: [],
  currentDeviceId: null,

  topics: [],
  togglingTopicId: null,
};

const profilePushNotificationsSlice = createSlice({
  name: "profilePushNotifications",
  initialState,
  reducers: {
    resetProfilePushNotificationsError: (state) => {
      state.error = null;
    },
    clearProfilePushNotifications: () => ({ ...initialState }),
  },
  extraReducers: (builder) => {
    // Sync
    builder.addCase(syncPushNotificationDeviceAndTopics.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(
      syncPushNotificationDeviceAndTopics.fulfilled,
      (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.providerDeviceId = action.payload?.providerDeviceId ?? null;
        state.devices = action.payload?.devices ?? [];
        state.deviceIds = action.payload?.deviceIds ?? [];
        state.currentDeviceId = action.payload?.currentDeviceId ?? null;
        state.topics = action.payload?.topics ?? [];
      }
    );
    builder.addCase(
      syncPushNotificationDeviceAndTopics.rejected,
      (state, a) => {
        state.isLoading = false;
        state.error = a.payload ?? a.error?.message ?? "Errore";
      }
    );

    // Lists (optional direct usage)
    builder.addCase(listPushNotificationDevices.fulfilled, (state, action) => {
      state.devices = action.payload?.devices ?? [];
    });
    builder.addCase(listPushNotificationTopics.fulfilled, (state, action) => {
      state.topics = action.payload?.topics ?? [];
    });

    // Delete device
    builder.addCase(deletePushNotificationDevice.fulfilled, (state, action) => {
      const deletedId = Number(action.payload?.deviceId);
      state.devices = state.devices.filter((d) => Number(d?.id) !== deletedId);
      state.deviceIds = state.deviceIds.filter((id) => id !== deletedId);
      if (state.currentDeviceId === deletedId) {
        state.currentDeviceId = null;
        state.providerDeviceId = null;
      }
    });

    // Subscribe / Unsubscribe
    // Optimistically flip the row's checked state (derived from
    // attributes.subscription.attributes.enabled) immediately on `pending`, so
    // the toggle feels instant. The trailing listPushNotificationTopics()
    // refetch reconciles (and supplies the real subscription.id); on a rejected
    // request that same refetch restores the true server state.
    const setTopicEnabled = (state, topicId, enabled) => {
      const id = Number(topicId);
      state.topics = (state.topics || []).map((t) => {
        if (Number(t?.id) !== id) return t;
        const sub = t?.attributes?.subscription || {};
        return {
          ...t,
          attributes: {
            ...(t?.attributes || {}),
            subscription: {
              ...sub,
              attributes: {
                ...(sub.attributes || {}),
                enabled,
              },
            },
          },
        };
      });
    };
    const setAllTopicsEnabled = (state, enabled) => {
      state.topics = (state.topics || []).map((t) => {
        const sub = t?.attributes?.subscription || {};
        return {
          ...t,
          attributes: {
            ...(t?.attributes || {}),
            subscription: {
              ...sub,
              attributes: {
                ...(sub.attributes || {}),
                enabled,
              },
            },
          },
        };
      });
    };

    const onTogglePending = (enabled) => (state, action) => {
      state.error = null;
      state.togglingTopicId = Number(action.meta?.arg?.topicId) || null;
      setTopicEnabled(state, action.meta?.arg?.topicId, enabled);
    };
    const onToggleRejected = (state, a) => {
      state.togglingTopicId = null;
      state.error = a.payload ?? a.error?.message ?? "Errore";
    };
    const onToggleFulfilled = (state) => {
      // Optimistic value already applied on pending; just clear the in-flight id.
      state.togglingTopicId = null;
    };

    builder.addCase(
      subscribeToPushNotificationTopic.pending,
      onTogglePending(true)
    );
    builder.addCase(
      subscribeToPushNotificationTopic.fulfilled,
      onToggleFulfilled
    );
    builder.addCase(
      subscribeToPushNotificationTopic.rejected,
      onToggleRejected
    );

    builder.addCase(
      unsubscribeFromPushNotificationTopic.pending,
      onTogglePending(false)
    );
    builder.addCase(
      unsubscribeFromPushNotificationTopic.fulfilled,
      onToggleFulfilled
    );
    builder.addCase(
      unsubscribeFromPushNotificationTopic.rejected,
      onToggleRejected
    );

    // Master toggle: flip every topic at once on pending.
    builder.addCase(subscribeToAllPushNotificationTopics.pending, (state) => {
      state.error = null;
      setAllTopicsEnabled(state, true);
    });
    builder.addCase(
      unsubscribeFromAllPushNotificationTopics.pending,
      (state) => {
        state.error = null;
        setAllTopicsEnabled(state, false);
      }
    );
  },
});

export const {
  resetProfilePushNotificationsError,
  clearProfilePushNotifications,
} = profilePushNotificationsSlice.actions;

export default profilePushNotificationsSlice.reducer;
